import React, { useState, useEffect, useCallback } from "react";
import { Dialog, Slide, IconButton } from "@mui/material";
import type { SlideProps } from "@mui/material/Slide";
import CloseIcon from "@mui/icons-material/Close";
import { ProductUI } from "@/shared/types";
import "./styles/ProductModal.scss";
import { formatPhotoUrl } from "../utils/formatter";
import { useCart } from "@/app/providers/CartProvider";
import { ProductPriceDisplay, ProductLineTotal } from "./ProductPriceDisplay";
import { getQuantityUnit } from "../utils/productPricing";

interface ProductModalProps {
  product: ProductUI | null;
  onClose: () => void;
  onAddToCart: (product: ProductUI, quantity: number) => void;
}

const SlideTransition = React.forwardRef(function Transition(
  props: SlideProps,
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const ProductModal: React.FC<ProductModalProps> = ({
  product,
  onClose,
}) => {
  const { getItemQuantity, setItemQuantity } = useCart();
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!product) return;
    queueMicrotask(() => {
      const savedQuantity = getItemQuantity(product.id);
      setQuantity(savedQuantity > 0 ? savedQuantity : 1);
    });
  }, [product, getItemQuantity]);

  const handleAddToCart = useCallback(() => {
    if (product) {
      setItemQuantity(product, quantity);
      onClose();
    }
  }, [product, quantity, setItemQuantity, onClose]);

  const increment = useCallback(() => {
    setQuantity((prev) => prev + 1);
  }, []);

  const decrement = useCallback(() => {
    setQuantity((prev) => Math.max(1, prev - 1));
  }, []);

  const quantityUnit = product ? getQuantityUnit(product) : "шт";

  if (!product) return null;

  const hasPhoto = product.photo && product.photo.trim() !== "";
  const imageUrl = formatPhotoUrl(product.photo);

  return (
    <Dialog
      open={!!product}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={SlideTransition}
      PaperProps={{
        className: "product-modal__dialog",
      }}
    >
      <div className="product-modal__content">
        <IconButton
          className="product-modal__close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <CloseIcon />
        </IconButton>

        <div className="product-modal__image">
          {hasPhoto ? (
            <img src={imageUrl} alt={product.name} />
          ) : (
            <div className="product-modal__no-image">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect
                  x="2"
                  y="2"
                  width="20"
                  height="20"
                  rx="2.18"
                  ry="2.18"
                ></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <span>Нет фото</span>
            </div>
          )}
        </div>

        <div className="product-modal__info">
          <h2 className="product-modal__title">{product.name}</h2>

          {product.description && (
            <p className="product-modal__description">{product.description}</p>
          )}

          <div className="product-modal__price-section">
            <ProductPriceDisplay product={product} quantity={quantity} />
          </div>

          <div className="product-modal__actions">
            <div className="product-modal__quantity">
              <button
                className="quantity-btn"
                onClick={decrement}
                disabled={quantity === 1}
              >
                -
              </button>
              <span className="quantity-value">
                {quantity} {quantityUnit}
              </span>
              <button className="quantity-btn" onClick={increment}>
                +
              </button>
            </div>

            <button
              className="product-modal__add-to-cart"
              onClick={handleAddToCart}
            >
              В корзину ·{" "}
              <ProductLineTotal product={product} quantity={quantity} />
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
